/**
 * Coverage AutoFill (CAR) — CAR-8 confidence tier formula.
 *
 * The Author Evidence dialog already infers a confidence band from
 * field-fill ratio via `inferAuthoredConfidence` (in
 * `src/types/authoredEvidence.ts`). That helper covers the
 * USER-AUTHORED path: the user filled some structured fields, the band
 * is derived from how many.
 *
 * CAR-8 adds the parallel AUTO-FILL path. When the platform fills the
 * requirement from a provider, the DAM, or a grounded AI inference, the
 * band must reflect HOW the data was obtained — not just the ratio.
 * A `'low'` AI-inferred fill is never elevated to `'high'` regardless
 * of field count, because a hallucinated value scoring 4/4 is still
 * less trustworthy than a 1/4 provider-direct fill backed by a public
 * source.
 *
 * Tier rules (pack §CAR-8):
 *   - `'ai-inference'`      → ALWAYS `'low'` (capped, never elevated).
 *   - `'dam-baseline'`      → `'medium'` (curated knowledge, not site-
 *                             specific data).
 *   - `'provider'` ≥ 100%   → `'high'`.
 *   - `'doc'` ≥ 100%        → `'high'` (uploaded doc fills all fields).
 *   - `'provider' | 'doc'`
 *     with ratio ≥ 50%      → `'medium'`.
 *   - everything else       → `'low'`.
 *
 * User overrides (the dropdown in the AuthorEvidenceDialog) ALWAYS win
 * regardless of fillSource — that override path lives upstream in
 * `authoredEvidenceWriter` and is not modelled here.
 *
 * Numeric values mirror `AUTHORED_CONFIDENCE_SCORE` (0.45 / 0.60 / 0.75)
 * so a Coverage Matrix row can render a single confidence number that
 * means the same thing across authored / autofilled / hybrid rows.
 */
import { AUTHORED_CONFIDENCE_SCORE } from '../../types/authoredEvidence';

/**
 * Where a coverage value came from. Mirrors the `fillSource` taxonomy
 * the CAR pack codifies for autofilled coverage entries. The values are
 * a superset of CAF's stub (`'provider' | 'doc' | 'dam-baseline' |
 * 'ai-inference' | 'authored' | 'unknown'`) so a hybrid row can still
 * declare an explicit source even when CAR hasn't fully wired it.
 */
export type CoverageFillSource =
  | 'provider'
  | 'doc'
  | 'dam-baseline'
  | 'ai-inference'
  | 'authored'
  | 'unknown';

/** Inputs to {@link inferAutoFillConfidence}. */
export interface InferAutoFillConfidenceInput {
  fillSource: CoverageFillSource;
  /** How many structured fields the autofiller populated. */
  fieldsFilledCount: number;
  /** Total fields the requirement's `authoredSchema` declares. */
  fieldsTotalCount: number;
  /**
   * CAR-8 follow-up — provider freshness in days since the chain
   * fetched the underlying data. When `>= 90`, the resolved tier is
   * downgraded one band (high → medium → low; low stays low) and the
   * rationale is annotated with the staleness reason. When `undefined`
   * (caller has no freshness signal) or `< 90`, behaviour is
   * unchanged.
   *
   * The 90d cap is a pack-level CONSTANT (R2). It is NOT an env var
   * or feature flag — staleness is a fact about the upstream data,
   * not a deploy-time tunable.
   *
   * Defensive: non-finite values (NaN / ±Infinity) are treated as
   * `undefined`. Negative values are also no-ops (caller's clock
   * skew, not a degradation signal).
   */
  providerFreshnessDays?: number;
}

/**
 * CAR-8 follow-up — pack-mandated freshness cap. Provider data older
 * than this triggers a one-tier downgrade on the autofill confidence
 * band. Pinned as a `const` so a refactor can't silently raise the bar.
 */
export const PROVIDER_FRESHNESS_CAP_DAYS = 90;

/** Output band + numeric score (parallel to `confidenceToScore`). */
export interface InferAutoFillConfidenceResult {
  tier: 'low' | 'medium' | 'high';
  value: 0.45 | 0.6 | 0.75;
  rationale: string;
}

/**
 * Compute the confidence band for an AUTO-FILLED coverage entry.
 *
 * The rules are deliberately conservative — a provider must fill EVERY
 * field to earn `'high'`, AI inference is capped at `'low'`, and the
 * DAM-baseline path is capped at `'medium'` because it carries general
 * knowledge rather than site-specific data.
 *
 * @param input  fillSource + field-fill ratio.
 * @returns      The band, its numeric score (matches
 *               `AUTHORED_CONFIDENCE_SCORE`), and a one-line rationale
 *               surfaced in the Coverage Matrix lineage tooltip.
 */
export function inferAutoFillConfidence(
  input: InferAutoFillConfidenceInput,
): InferAutoFillConfidenceResult {
  const { fillSource, fieldsFilledCount, fieldsTotalCount, providerFreshnessDays } = input;

  // Defensive: never divide by zero. A requirement with no structured
  // fields collapses to a prose-only row — the DAM-baseline/provider
  // path can still attach a source, so we honour the fillSource but
  // pin the band to the source-class default.
  const ratio = fieldsTotalCount > 0 ? fieldsFilledCount / fieldsTotalCount : 0;
  const ratioPct = Math.round(ratio * 100);

  // CAR-8 follow-up: compute the raw band via the inline branch
  // structure below, then optionally degrade for staleness via
  // `applyFreshnessCap`. Keeping the existing branches untouched
  // preserves the original behaviour 1-for-1 when
  // `providerFreshnessDays` is `undefined`.
  const raw = computeRawTier();
  return applyFreshnessCap(raw, providerFreshnessDays);

  function computeRawTier(): InferAutoFillConfidenceResult {

  // ── AI inference: ALWAYS low, regardless of ratio. ─────────────────
  // Hallucination guard — a model that fills 4/4 fields without
  // grounding is still untrusted; the inline source chip + medium band
  // would mislead the report writer.
  if (fillSource === 'ai-inference') {
    return {
      tier: 'low',
      value: AUTHORED_CONFIDENCE_SCORE.low as 0.45,
      rationale: `AI-inferred fill (${fieldsFilledCount}/${fieldsTotalCount} fields) — capped at low until a real source confirms. Upload a real document or wait for provider data.`,
    };
  }

  // ── Provider-direct: high when fully filled, otherwise medium ≥50%. ─
  if (fillSource === 'provider') {
    if (fieldsTotalCount > 0 && fieldsFilledCount >= fieldsTotalCount) {
      return {
        tier: 'high',
        value: AUTHORED_CONFIDENCE_SCORE.high as 0.75,
        rationale: `Provider-direct fill (${fieldsFilledCount}/${fieldsTotalCount} fields, ${ratioPct}%). Authoritative public source — high confidence pending real document.`,
      };
    }
    if (ratio >= 0.5) {
      return {
        tier: 'medium',
        value: AUTHORED_CONFIDENCE_SCORE.medium as 0.6,
        rationale: `Provider-direct fill (${fieldsFilledCount}/${fieldsTotalCount} fields, ${ratioPct}%). Partial coverage — medium confidence. Remaining fields require upload or authored entry.`,
      };
    }
    return {
      tier: 'low',
      value: AUTHORED_CONFIDENCE_SCORE.low as 0.45,
      rationale: `Provider-direct fill (${fieldsFilledCount}/${fieldsTotalCount} fields, ${ratioPct}%). Too few fields populated for medium confidence.`,
    };
  }

  // ── Uploaded doc: same ladder as provider. ─────────────────────────
  if (fillSource === 'doc') {
    if (fieldsTotalCount > 0 && fieldsFilledCount >= fieldsTotalCount) {
      return {
        tier: 'high',
        value: AUTHORED_CONFIDENCE_SCORE.high as 0.75,
        rationale: `Doc-extracted fill (${fieldsFilledCount}/${fieldsTotalCount} fields, ${ratioPct}%). Uploaded document covers every field — high confidence.`,
      };
    }
    if (ratio >= 0.5) {
      return {
        tier: 'medium',
        value: AUTHORED_CONFIDENCE_SCORE.medium as 0.6,
        rationale: `Doc-extracted fill (${fieldsFilledCount}/${fieldsTotalCount} fields, ${ratioPct}%). Document covers some fields — medium confidence.`,
      };
    }
    return {
      tier: 'low',
      value: AUTHORED_CONFIDENCE_SCORE.low as 0.45,
      rationale: `Doc-extracted fill (${fieldsFilledCount}/${fieldsTotalCount} fields, ${ratioPct}%). Document covers too few fields for medium confidence.`,
    };
  }

  // ── DAM-baseline: capped at medium (curated, not site-specific). ───
  if (fillSource === 'dam-baseline') {
    return {
      tier: 'medium',
      value: AUTHORED_CONFIDENCE_SCORE.medium as 0.6,
      rationale: `DAM curated baseline (${fieldsFilledCount}/${fieldsTotalCount} fields). General-knowledge ranges — medium confidence. Site-specific data required for high.`,
    };
  }

  // ── Authored: handled upstream (`inferAuthoredConfidence`); fall
  //    through here defensively if a caller routes 'authored' to us. ───
  if (fillSource === 'authored') {
    if (ratio >= 0.8) {
      return {
        tier: 'high',
        value: AUTHORED_CONFIDENCE_SCORE.high as 0.75,
        rationale: `Authored evidence (${fieldsFilledCount}/${fieldsTotalCount} fields, ${ratioPct}%).`,
      };
    }
    if (ratio >= 0.4) {
      return {
        tier: 'medium',
        value: AUTHORED_CONFIDENCE_SCORE.medium as 0.6,
        rationale: `Authored evidence (${fieldsFilledCount}/${fieldsTotalCount} fields, ${ratioPct}%).`,
      };
    }
    return {
      tier: 'low',
      value: AUTHORED_CONFIDENCE_SCORE.low as 0.45,
      rationale: `Authored evidence (${fieldsFilledCount}/${fieldsTotalCount} fields, ${ratioPct}%).`,
    };
  }

  // ── Unknown / missing source. ──────────────────────────────────────
  return {
    tier: 'low',
    value: AUTHORED_CONFIDENCE_SCORE.low as 0.45,
    rationale: `No identified source for fill — capped at low.`,
  };
  }
}

/**
 * CAR-8 follow-up — apply the 90d freshness cap on top of a raw band.
 *
 * Degradation ladder (one-tier downgrade when stale):
 *   - `high`   (0.75) → `medium` (0.60)
 *   - `medium` (0.60) → `low`    (0.45)
 *   - `low`   stays   `low`      (already floored)
 *
 * The rationale string is annotated with the exact staleness in days
 * so the Coverage Matrix lineage tooltip explains the downgrade. The
 * original rationale is preserved verbatim before the annotation so
 * the source-class summary (fields filled / ratio) is not lost.
 *
 * Defensive rules (HARD RULE 8 — no silent surprises):
 *   - `undefined` → unchanged (caller has no freshness signal).
 *   - `NaN` / `±Infinity` → unchanged (treated as `undefined`).
 *   - Negative values → unchanged (caller's clock skew, not stale data).
 *   - The threshold is INCLUSIVE at exactly 90 (per pack spec: data
 *     "<90d" is fresh; therefore 90d itself triggers the downgrade).
 */
export function applyFreshnessCap(
  raw: InferAutoFillConfidenceResult,
  providerFreshnessDays: number | undefined,
): InferAutoFillConfidenceResult {
  // No-op when the signal is missing or not a finite positive number.
  if (
    providerFreshnessDays === undefined ||
    !Number.isFinite(providerFreshnessDays) ||
    (providerFreshnessDays as number) < PROVIDER_FRESHNESS_CAP_DAYS
  ) {
    return raw;
  }

  const ageDays = providerFreshnessDays as number;
  // Round for human-readable rationale; preserves the dimensional
  // intent of the input without leaking sub-day noise.
  const ageDaysRounded = Math.round(ageDays);

  if (raw.tier === 'high') {
    return {
      tier: 'medium',
      value: AUTHORED_CONFIDENCE_SCORE.medium as 0.6,
      rationale:
        `${raw.rationale} ` +
        `Provider data is ${ageDaysRounded} days old; downgraded to medium per ${PROVIDER_FRESHNESS_CAP_DAYS}d freshness cap.`,
    };
  }
  if (raw.tier === 'medium') {
    return {
      tier: 'low',
      value: AUTHORED_CONFIDENCE_SCORE.low as 0.45,
      rationale:
        `${raw.rationale} ` +
        `Provider data is ${ageDaysRounded} days old; downgraded to low per ${PROVIDER_FRESHNESS_CAP_DAYS}d freshness cap.`,
    };
  }
  // tier === 'low' — already floored, no further degrade possible.
  return raw;
}
